/**
 * FAA Class B/C/D/E5 airspace, fetched through our backend proxy
 * (/api/airspace/query). The proxy handles ArcGIS upstream + caches by
 * (where, rounded-bbox) so:
 *   - Browser sees same-origin response (no CORS surprises on upstream 5xx)
 *   - Repeated pans within a 0.1° window hit cache (~10ms vs ~600ms cold)
 *   - Upstream 504s serve stale cache instead of failing the overlay
 */

const CLASS_SERVICE = "/api/airspace/query";

export type AirspaceClassId = "classB" | "classC" | "classD" | "classE5";

export interface AirspaceClassConfig {
  id: AirspaceClassId;
  label: string;
  where: string;
  color: string;
  defaultOn: boolean;
}

export const AIRSPACE_CLASSES: AirspaceClassConfig[] = [
  { id: "classB", label: "Class B",       where: "CLASS='B'",            color: "#3b82f6", defaultOn: true  },
  { id: "classC", label: "Class C",       where: "CLASS='C'",            color: "#a855f7", defaultOn: true  },
  { id: "classD", label: "Class D",       where: "CLASS='D'",            color: "#06b6d4", defaultOn: true  },
  { id: "classE5", label: "Class E5 (700' AGL)", where: "LOCAL_TYPE='CLASS_E5'", color: "#6b7280", defaultOn: false },
];

export interface Bbox4326 {
  lon_min: number; lat_min: number; lon_max: number; lat_max: number;
}

/** Query one airspace class inside a bbox. Returns GeoJSON FeatureCollection.
 * Hits the backend proxy at /api/airspace/query — see backend/app/api/airspace.py. */
export async function fetchAirspace(
  cls: AirspaceClassConfig,
  bbox: Bbox4326,
  signal?: AbortSignal,
): Promise<GeoJSON.FeatureCollection> {
  const params = new URLSearchParams({
    where: cls.where,
    bbox: `${bbox.lon_min},${bbox.lat_min},${bbox.lon_max},${bbox.lat_max}`,
  });
  const res = await fetch(`${CLASS_SERVICE}?${params}`, { signal });
  if (!res.ok) throw new Error(`Airspace fetch failed: ${res.status} ${res.statusText}`);
  const json: GeoJSON.FeatureCollection = await res.json();
  for (const f of json.features ?? []) {
    f.properties = { ...(f.properties ?? {}), __cls: cls.id, __color: cls.color };
  }
  return json;
}
